import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Activity, AlertTriangle, CheckCircle2, RefreshCw, Wrench, ShieldAlert, Heart, Bell, Link2 } from "lucide-react";

const ADMIN_EMAIL = "lorenjordan333@gmail.com";

type FeedEvent = {
  id: string;
  created_at: string;
  event_type: string;
  severity: "debug" | "info" | "warn" | "error" | "critical";
  entity_type?: string | null;
  entity_id?: string | null;
  client_id?: string | null;
  client_name?: string | null;
  client_url?: string | null;
  title: string;
  message?: string | null;
  metadata?: Record<string, any> | null;
  source?: string | null;
  correlation_id?: string | null;
  repair_attempt?: number | null;
  validator_code?: string | null;
};

const statusColor: Record<string, string> = {
  DRAFT: "bg-muted text-muted-foreground",
  VALIDATED: "bg-blue-500/20 text-blue-300 border-blue-500/40",
  READY: "bg-cyan-500/20 text-cyan-300 border-cyan-500/40",
  PUBLISHED: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  INDEXED: "bg-green-500/20 text-green-300 border-green-500/40",
  REJECTED: "bg-red-500/20 text-red-300 border-red-500/40",
  NEEDS_REVIEW: "bg-amber-500/20 text-amber-300 border-amber-500/40",
};

const severityDot: Record<string, string> = {
  debug: "bg-muted",
  info: "bg-cyan-400",
  warn: "bg-amber-400",
  error: "bg-red-400",
  critical: "bg-red-500",
};

const eventTypeColor: Record<string, string> = {
  article_generated: "bg-blue-400",
  validation_started: "bg-cyan-400",
  validation_passed: "bg-emerald-400",
  validation_failed: "bg-red-400",
  repair_started: "bg-amber-400",
  repair_applied: "bg-amber-300",
  repair_failed: "bg-red-400",
  publish_succeeded: "bg-green-400",
  publish_reconfirmed: "bg-emerald-300/70",
  publish_rejected: "bg-red-500",
  needs_review_created: "bg-amber-500",
  escalation_triggered: "bg-amber-500",
  indexing_submitted: "bg-green-300",
  indexing_confirmed: "bg-green-400",
  operator_approved: "bg-emerald-400",
  operator_rejected: "bg-red-400",
  retry_requested: "bg-cyan-400",
  nightly_revalidation_started: "bg-blue-400",
  nightly_revalidation_failed: "bg-red-400",
  alert_created: "bg-amber-500",
  slug_normalized: "bg-purple-400",
  canonical_fixed: "bg-purple-400",
  internal_links_removed: "bg-purple-400",
};

type FeedCategory = "generation" | "revalidation" | "indexing" | "audit" | "other";

const CATEGORY_META: Record<FeedCategory, { label: string; badge: string }> = {
  generation:   { label: "Generation",   badge: "bg-blue-500/15 text-blue-300 border-blue-500/30" },
  revalidation: { label: "Revalidation", badge: "bg-purple-500/15 text-purple-300 border-purple-500/30" },
  indexing:     { label: "Indexing",     badge: "bg-green-500/15 text-green-300 border-green-500/30" },
  audit:        { label: "Audit",        badge: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  other:        { label: "Other",        badge: "bg-muted text-muted-foreground border-border" },
};

function categorize(e: FeedEvent): FeedCategory {
  const src = (e.source || "").toLowerCase();
  if (src.includes("nightly-revalidation") || src.includes("revalidate-published")) return "revalidation";
  if (src.includes("push-to-indexing")) return "indexing";
  if (src.includes("daily-link-audit") || src.includes("audit")) return "audit";
  if (e.event_type === "publish_reconfirmed") return "revalidation";
  if (
    src.includes("validation-brain") ||
    src.includes("generate-first-articles") ||
    src.includes("generate-daily-article") ||
    src.includes("generate-citation-pages") ||
    e.event_type === "article_generated"
  ) return "generation";
  return "other";
}

function timeAgo(iso: string) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

const BrainCenter = () => {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("feed");

  const [overview, setOverview] = useState<any>(null);
  const [feed, setFeed] = useState<FeedEvent[] | null>(null);
  const [review, setReview] = useState<any[] | null>(null);
  const [analytics, setAnalytics] = useState<any>(null);
  const [health, setHealth] = useState<any>(null);
  const [alerts, setAlerts] = useState<any[] | null>(null);

  const [inspect, setInspect] = useState<any | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const invoke = useCallback(async (action: string, extra: any = {}) => {
    const { data, error } = await supabase.functions.invoke("admin-brain", { body: { action, ...extra } });
    if (error) {
      toast({ title: "Brain Center error", description: error.message, variant: "destructive" });
      return null;
    }
    return data;
  }, []);

  const reload = useCallback(async () => {
    const [o, f, r, a, h, al] = await Promise.all([
      invoke("overview"),
      invoke("feed", { limit: 80 }),
      invoke("needs_review"),
      invoke("analytics"),
      invoke("health"),
      invoke("alerts"),
    ]);
    if (o) setOverview(o);
    if (f) setFeed(f.events ?? []);
    if (r) setReview(r.items ?? []);
    if (a) setAnalytics(a);
    if (h) setHealth(h);
    if (al) setAlerts(al.alerts ?? []);
  }, [invoke]);

  useEffect(() => {
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { navigate("/auth", { replace: true }); return; }
      if ((session.user.email ?? "").toLowerCase() !== ADMIN_EMAIL) {
        toast({ title: "Forbidden", variant: "destructive" });
        navigate("/dashboard", { replace: true });
        return;
      }
      setAuthed(true);
      await reload();
    })();
  }, [navigate, reload]);

  useEffect(() => {
    if (!authed || !autoRefresh) return;
    const id = setInterval(reload, 15000);
    return () => clearInterval(id);
  }, [authed, autoRefresh, reload]);

  const doAction = async (action: string, postId: string, kind: string, extra: any = {}) => {
    const res = await invoke(action, { post_id: postId, kind, ...extra });
    if (res?.ok || res?.status === 200) toast({ title: `${action} ok` });
    await reload();
  };

  if (!authed) {
    return (
      <div className="min-h-screen p-8 space-y-4 bg-background">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-card/40 backdrop-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-3 h-3 rounded-full bg-emerald-400 animate-pulse" />
              <div className="absolute inset-0 w-3 h-3 rounded-full bg-emerald-400/50 animate-ping" />
            </div>
            <div>
              <h1 className="text-lg font-semibold tracking-tight">OrbitRank Brain Center</h1>
              <p className="text-xs text-muted-foreground">Autonomous validation · repair · publishing</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={autoRefresh ? "default" : "outline"}
              size="sm"
              onClick={() => setAutoRefresh((v) => !v)}
            >
              <RefreshCw className={`h-4 w-4 ${autoRefresh ? "animate-spin" : ""}`} />
              {autoRefresh ? "Live" : "Paused"}
            </Button>
            <Button variant="outline" size="sm" onClick={reload}>Refresh</Button>
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin")}>← Admin</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        <OverviewStrip overview={overview} health={health} />

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-7 w-full max-w-4xl">
            <TabsTrigger value="feed"><Activity className="h-4 w-4 mr-1" />Feed</TabsTrigger>
            <TabsTrigger value="review">
              <ShieldAlert className="h-4 w-4 mr-1" />Review
              {review && review.length > 0 && (
                <span className="ml-1 text-xs bg-amber-500/20 text-amber-300 rounded px-1.5">{review.length}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="analytics"><Wrench className="h-4 w-4 mr-1" />Analytics</TabsTrigger>
            <TabsTrigger value="slugs"><Link2 className="h-4 w-4 mr-1" />Slugs</TabsTrigger>
            <TabsTrigger value="health"><Heart className="h-4 w-4 mr-1" />Health</TabsTrigger>
            <TabsTrigger value="alerts">
              <Bell className="h-4 w-4 mr-1" />Alerts
              {alerts && alerts.filter(a => !a.acknowledged).length > 0 && (
                <span className="ml-1 text-xs bg-red-500/20 text-red-300 rounded px-1.5">
                  {alerts.filter(a => !a.acknowledged).length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="status"><CheckCircle2 className="h-4 w-4 mr-1" />Status</TabsTrigger>
          </TabsList>

          <TabsContent value="feed" className="mt-4">
            <FeedView feed={feed} onInspect={setInspect} />
          </TabsContent>
          <TabsContent value="review" className="mt-4">
            <ReviewQueue items={review} onAction={doAction} onInspect={setInspect} />
          </TabsContent>
          <TabsContent value="analytics" className="mt-4">
            <AnalyticsView a={analytics} />
          </TabsContent>
          <TabsContent value="slugs" className="mt-4">
            <SlugAnalyticsView />
          </TabsContent>
          <TabsContent value="health" className="mt-4">
            <HealthView h={health} />
          </TabsContent>
          <TabsContent value="alerts" className="mt-4">
            <AlertsView alerts={alerts} onAck={async (id) => { await invoke("ack_alert", { alert_id: id }); reload(); }} />
          </TabsContent>
          <TabsContent value="status" className="mt-4">
            <StatusView overview={overview} />
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={!!inspect} onOpenChange={(o) => !o && setInspect(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{inspect?.title || "Lifecycle inspection"}</DialogTitle>
          </DialogHeader>
          {inspect && <InspectView item={inspect} onAction={doAction} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const OverviewStrip = ({ overview, health }: any) => {
  const tiles = useMemo(() => {
    const s = overview?.blog_status ?? {};
    const c = overview?.citation_status ?? {};
    const sum = (k: string) => (s[k] ?? 0) + (c[k] ?? 0);
    return [
      { label: "Published", value: sum("PUBLISHED") + sum("INDEXED"), color: "text-emerald-300" },
      { label: "In Queue", value: sum("DRAFT") + sum("VALIDATED") + sum("READY"), color: "text-blue-300" },
      { label: "Needs Review", value: sum("NEEDS_REVIEW"), color: "text-amber-300" },
      { label: "Rejected", value: sum("REJECTED"), color: "text-red-300" },
      { label: "Open Alerts", value: overview?.open_alerts ?? 0, color: "text-pink-300" },
      { label: "Broken Links", value: health?.broken_link_signals ?? 0, color: "text-orange-300" },
    ];
  }, [overview, health]);
  return (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
      {tiles.map((t) => (
        <Card key={t.label} className="bg-card/60">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{t.label}</div>
            <div className={`text-2xl font-bold mt-1 ${t.color}`}>{t.value}</div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

type FeedFilter = "all" | FeedCategory;

const FeedView = ({ feed, onInspect }: { feed: FeedEvent[] | null; onInspect: (e: any) => void }) => {
  const [filter, setFilter] = useState<FeedFilter>("all");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const { groups, counts } = useMemo(() => {
    const counts: Record<FeedFilter, number> = {
      all: 0, generation: 0, revalidation: 0, indexing: 0, audit: 0, other: 0,
    };
    const list = feed ?? [];
    for (const e of list) { counts.all++; counts[categorize(e)]++; }

    const filtered = filter === "all" ? list : list.filter((e) => categorize(e) === filter);

    // Group by correlation_id; events without one stand alone.
    const byCid = new Map<string, FeedEvent[]>();
    const order: string[] = [];
    for (const e of filtered) {
      const key = e.correlation_id || `_solo_${e.id}`;
      if (!byCid.has(key)) { byCid.set(key, []); order.push(key); }
      byCid.get(key)!.push(e);
    }
    const groups = order.map((k) => {
      const evs = byCid.get(k)!.slice().sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
      return { key: k, events: evs, solo: k.startsWith("_solo_") };
    });
    return { groups, counts };
  }, [feed, filter]);

  if (!feed) return <Skeleton className="h-96 w-full" />;

  const filters: { id: FeedFilter; label: string }[] = [
    { id: "all", label: "All" },
    { id: "generation", label: "Generation" },
    { id: "revalidation", label: "Revalidation" },
    { id: "indexing", label: "Indexing" },
    { id: "audit", label: "Audit" },
    { id: "other", label: "Other" },
  ];

  return (
    <Card>
      <CardContent className="p-0">
        <div className="flex flex-wrap gap-2 px-4 py-3 border-b border-border bg-card/40">
          {filters.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                filter === f.id
                  ? "bg-primary/15 text-primary border-primary/40"
                  : "bg-muted/30 text-muted-foreground border-border hover:bg-muted/50"
              }`}
            >
              {f.label}
              <span className="ml-1.5 opacity-60 tabular-nums">{counts[f.id]}</span>
            </button>
          ))}
        </div>

        {!groups.length ? (
          <p className="text-muted-foreground text-sm p-4">No events match this filter.</p>
        ) : (
          <div className="divide-y divide-border max-h-[60vh] overflow-y-auto">
            {groups.map((g) => {
              if (g.solo) {
                const e = g.events[0];
                return <FeedRow key={e.id} e={e} onInspect={onInspect} indent={false} />;
              }
              const head = g.events[0];
              const cat = categorize(head);
              const isOpen = expanded[g.key] ?? false;
              const headerLabel = head.entity_id
                ? `${g.events.length} events · ${head.entity_type || "entity"}`
                : `${g.events.length} events`;
              return (
                <div key={g.key}>
                  <button
                    onClick={() => setExpanded((s) => ({ ...s, [g.key]: !isOpen }))}
                    className="w-full text-left flex items-center gap-4 px-4 py-3 hover:bg-muted/30 transition-colors"
                  >
                    <div className={`w-2 h-2 rounded-full ${eventTypeColor[head.event_type] ?? "bg-muted"} shrink-0`} />
                    <div className="w-24 text-xs text-muted-foreground tabular-nums">{timeAgo(head.created_at)} ago</div>
                    <Badge variant="outline" className={`text-[10px] uppercase tracking-wide ${CATEGORY_META[cat].badge}`}>
                      {CATEGORY_META[cat].label}
                    </Badge>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">
                        {head.title}
                        <span className="text-muted-foreground ml-2 text-xs">— {headerLabel}</span>
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {[head.client_name, head.source].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground">{isOpen ? "▾" : "▸"}</span>
                  </button>
                  {isOpen && (
                    <div className="bg-muted/10">
                      {g.events.map((e) => (
                        <FeedRow key={e.id} e={e} onInspect={onInspect} indent />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const FeedRow = ({ e, onInspect, indent }: { e: FeedEvent; onInspect: (e: any) => void; indent: boolean }) => {
  const cat = categorize(e);
  return (
    <button
      onClick={() => onInspect(e)}
      className={`w-full text-left flex items-center gap-4 px-4 py-2.5 hover:bg-muted/30 transition-colors ${indent ? "pl-10" : ""}`}
    >
      <div className={`w-2 h-2 rounded-full ${eventTypeColor[e.event_type] ?? severityDot[e.severity] ?? "bg-muted"} shrink-0`} />
      <div className="w-24 text-xs text-muted-foreground tabular-nums">{timeAgo(e.created_at)} ago</div>
      <div className="w-44 text-xs uppercase tracking-wide text-muted-foreground truncate">{e.event_type.replace(/_/g, " ")}</div>
      {!indent && (
        <Badge variant="outline" className={`text-[10px] uppercase tracking-wide ${CATEGORY_META[cat].badge}`}>
          {CATEGORY_META[cat].label}
        </Badge>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-sm truncate">{e.title}</div>
        <div className="text-xs text-muted-foreground truncate">
          {e.message || [e.client_name, e.entity_type, e.source].filter(Boolean).join(" · ")}
        </div>
      </div>
      {e.severity === "error" || e.severity === "critical" ? (
        <Badge variant="outline" className="bg-red-500/20 text-red-300 border-red-500/40">{e.severity}</Badge>
      ) : e.severity === "warn" ? (
        <Badge variant="outline" className="bg-amber-500/20 text-amber-300 border-amber-500/40">warn</Badge>
      ) : null}
      {(e.repair_attempt ?? 0) > 0 && (
        <Badge variant="outline" className="text-xs">
          <Wrench className="h-3 w-3 mr-1" />#{e.repair_attempt}
        </Badge>
      )}
    </button>
  );
};

const ReviewQueue = ({ items, onAction, onInspect }: any) => {
  if (!items) return <Skeleton className="h-96 w-full" />;
  if (!items.length) return (
    <div className="text-center py-12 text-muted-foreground">
      <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-emerald-400/60" />
      No content needs review. System is healthy.
    </div>
  );
  return (
    <div className="space-y-3">
      {items.map((it: any) => (
        <Card key={it.id}>
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <CardTitle className="text-base truncate">{it.title}</CardTitle>
                <div className="text-xs text-muted-foreground mt-1">
                  {it.client_name} · /{it.slug} · {it.kind}
                </div>
              </div>
              <Badge variant="outline" className={statusColor[it.publish_status]}>{it.publish_status}</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {it.escalation_reason && (
              <div className="text-xs flex items-start gap-2 bg-amber-500/5 border border-amber-500/20 rounded p-2">
                <AlertTriangle className="h-3 w-3 text-amber-400 mt-0.5 shrink-0" />
                <span>{it.escalation_reason}</span>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3 text-xs">
              <div><span className="text-muted-foreground">Repair attempts:</span> {it.repair_attempts ?? 0}</div>
              <div><span className="text-muted-foreground">Errors:</span> {(it.validation_errors ?? []).length}</div>
              <div><span className="text-muted-foreground">Signatures:</span> {(it.failure_signatures ?? []).length}</div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={() => onInspect(it)}>Inspect</Button>
              <Button size="sm" variant="outline" onClick={() => onAction("retry", it.id, it.kind)}>
                <RefreshCw className="h-3 w-3" />Retry
              </Button>
              <Button size="sm" onClick={() => onAction("approve", it.id, it.kind)}>
                Approve & Publish
              </Button>
              <Button size="sm" variant="destructive" onClick={() => onAction("reject", it.id, it.kind, { reason: "operator_rejected" })}>
                Reject
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const AnalyticsView = ({ a }: any) => {
  if (!a) return <Skeleton className="h-96 w-full" />;
  const pct = (n: number) => `${(n * 100).toFixed(1)}%`;
  const route = a.route ?? { repairs: 0, failures: 0, published_despite_failed: 0 };
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <Card><CardContent className="p-4">
          <div className="text-xs text-muted-foreground uppercase">Success rate</div>
          <div className="text-3xl font-bold text-emerald-300">{pct(a.rates.success)}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-muted-foreground uppercase">Rejection rate</div>
          <div className="text-3xl font-bold text-red-300">{pct(a.rates.reject)}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-muted-foreground uppercase">Needs review</div>
          <div className="text-3xl font-bold text-amber-300">{pct(a.rates.needs_review)}</div>
        </CardContent></Card>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card><CardContent className="p-4">
          <div className="text-xs text-muted-foreground uppercase">Route repairs</div>
          <div className="text-2xl font-bold text-sky-300">{route.repairs}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-muted-foreground uppercase">Route failures</div>
          <div className="text-2xl font-bold text-orange-300">{route.failures}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-muted-foreground uppercase">Published despite failed validation</div>
          <div className={`text-2xl font-bold ${route.published_despite_failed > 0 ? "text-red-400" : "text-emerald-300"}`}>
            {route.published_despite_failed}
          </div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-muted-foreground uppercase">Categories w/o handler</div>
          <div className={`text-2xl font-bold ${(a.unhandled_categories?.length ?? 0) > 0 ? "text-amber-300" : "text-emerald-300"}`}>
            {a.unhandled_categories?.length ?? 0}
          </div>
        </CardContent></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TopList title="Top validator failures" data={a.top_errors} />
        <TopList title="Top repair categories" data={a.top_repairs} />
        <TopList title="Top failure signatures" data={a.top_failure_signatures} />
        <TopList title="Repair categories without handlers" data={a.unhandled_categories} />
        <Card>
          <CardHeader><CardTitle className="text-sm">Top failing clients</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Client</TableHead><TableHead>Total</TableHead>
                <TableHead>Rej</TableHead><TableHead>Review</TableHead><TableHead>Fail %</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(a.top_failing_clients ?? []).map((c: any) => (
                  <TableRow key={c.client_id}>
                    <TableCell className="truncate max-w-[160px]">{c.name}</TableCell>
                    <TableCell>{c.total}</TableCell>
                    <TableCell className="text-red-300">{c.rejected}</TableCell>
                    <TableCell className="text-amber-300">{c.needs_review}</TableCell>
                    <TableCell>{pct(c.fail_rate)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const TopList = ({ title, data }: { title: string; data: any[] }) => (
  <Card>
    <CardHeader><CardTitle className="text-sm">{title}</CardTitle></CardHeader>
    <CardContent>
      {!data?.length && <p className="text-xs text-muted-foreground">No data.</p>}
      <div className="space-y-1">
        {(data ?? []).map((d) => (
          <div key={d.key} className="flex items-center gap-2 text-xs">
            <code className="flex-1 truncate">{d.key}</code>
            <span className="font-mono text-muted-foreground">{d.count}</span>
            <div className="w-24 h-1.5 bg-muted rounded">
              <div className="h-full bg-primary rounded" style={{ width: `${Math.min(100, d.count * 8)}%` }} />
            </div>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

const HealthView = ({ h }: any) => {
  if (!h) return <Skeleton className="h-96 w-full" />;
  const items = [
    { label: "Sitemap (published)", value: h.sitemap_published, ok: h.sitemap_published >= 0 },
    { label: "Publishing queue", value: h.publishing_queue, ok: h.publishing_queue < 50 },
    { label: "Validation queue", value: h.validation_queue, ok: h.validation_queue < 50 },
    { label: "Needs review queue", value: h.needs_review_queue, ok: h.needs_review_queue < 10 },
    { label: "Rejected total", value: h.rejected_total, ok: true },
    { label: "Broken link signals", value: h.broken_link_signals, ok: h.broken_link_signals < 5 },
    { label: "Revalidations (24h)", value: h.nightly_revalidations_24h, ok: h.nightly_revalidations_24h > 0 },
    { label: "Alerts (7d)", value: h.alerts_7d, ok: h.alerts_open === 0 },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {items.map((it) => (
        <Card key={it.label}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs text-muted-foreground">{it.label}</div>
              <div className={`w-2 h-2 rounded-full ${it.ok ? "bg-emerald-400" : "bg-amber-400"}`} />
            </div>
            <div className="text-2xl font-bold">{it.value}</div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const AlertsView = ({ alerts, onAck }: any) => {
  if (!alerts) return <Skeleton className="h-96 w-full" />;
  if (!alerts.length) return (
    <div className="text-center py-12 text-muted-foreground">
      <Bell className="h-12 w-12 mx-auto mb-3 text-muted-foreground/40" />
      No alerts. Alert architecture ready — emitters will write here.
    </div>
  );
  return (
    <div className="space-y-2">
      {alerts.map((a: any) => (
        <Card key={a.id} className={a.acknowledged ? "opacity-50" : ""}>
          <CardContent className="p-3 flex items-center gap-3">
            <Badge variant="outline" className={
              a.severity === "critical" ? "bg-red-500/20 text-red-300 border-red-500/40"
                : a.severity === "warn" ? "bg-amber-500/20 text-amber-300 border-amber-500/40"
                : "bg-blue-500/20 text-blue-300 border-blue-500/40"
            }>{a.severity}</Badge>
            <div className="flex-1 min-w-0">
              <div className="text-sm">{a.title}</div>
              {a.message && <div className="text-xs text-muted-foreground truncate">{a.message}</div>}
            </div>
            <div className="text-xs text-muted-foreground">{timeAgo(a.created_at)} ago</div>
            {!a.acknowledged && (
              <Button size="sm" variant="outline" onClick={() => onAck(a.id)}>Ack</Button>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const StatusView = ({ overview }: any) => {
  if (!overview) return <Skeleton className="h-96 w-full" />;
  const Block = ({ title, data }: any) => (
    <Card>
      <CardHeader><CardTitle className="text-sm">{title}</CardTitle></CardHeader>
      <CardContent>
        <div className="space-y-2">
          {Object.entries(data ?? {}).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between text-sm">
              <Badge variant="outline" className={statusColor[k] ?? ""}>{k}</Badge>
              <span className="font-mono">{v as any}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Block title="Blog posts by status" data={overview.blog_status} />
      <Block title="Citation pages by status" data={overview.citation_status} />
    </div>
  );
};

const InspectView = ({ item, onAction }: any) => {
  const errors = item.errors ?? item.validation_errors ?? [];
  const history = item.repair_history ?? [];
  // Resolve entity for timeline lookup. Feed events carry entity_type/entity_id;
  // Needs-Review rows carry id + kind.
  const entityId: string | undefined = item.entity_id ?? item.id ?? item.post_id;
  const entityKind: string = item.entity_type === "citation_page" || item.kind === "citation" ? "citation" : "blog";
  const [timeline, setTimeline] = useState<any[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!entityId) return;
      const { data } = await supabase.functions.invoke("admin-brain", {
        body: { action: "timeline", post_id: entityId, kind: entityKind },
      });
      if (!cancelled) setTimeline(data?.events ?? []);
    })();
    return () => { cancelled = true; };
  }, [entityId, entityKind]);
  return (
    <div className="space-y-4 text-sm">
      <div className="flex items-center gap-2 flex-wrap">
        <Badge variant="outline" className={statusColor[item.status ?? item.publish_status] ?? ""}>
          {item.status ?? item.publish_status ?? item.event_type}
        </Badge>
        <span className="text-xs text-muted-foreground">
          {(item.client_name ?? "—")} · {item.slug ? `/${item.slug}` : (item.title ?? "")}
        </span>
      </div>
      {item.escalation_reason && (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded p-2 text-xs">
          <strong>Escalation:</strong> {item.escalation_reason}
        </div>
      )}
      {item.final_failure_reason && (
        <div className="bg-red-500/5 border border-red-500/20 rounded p-2 text-xs">
          <strong>Final failure:</strong> {item.final_failure_reason}
        </div>
      )}

      {entityId && (
        <section>
          <h3 className="font-semibold mb-1">Lifecycle timeline {timeline ? `(${timeline.length})` : ""}</h3>
          {!timeline ? (
            <Skeleton className="h-24 w-full" />
          ) : timeline.length === 0 ? (
            <p className="text-xs text-muted-foreground">No events recorded yet.</p>
          ) : (
            <div className="max-h-56 overflow-y-auto border border-border rounded">
              {timeline.map((ev: any) => (
                <div key={ev.id} className="flex items-center gap-2 px-2 py-1 text-xs border-b last:border-b-0 border-border/60">
                  <div className={`w-1.5 h-1.5 rounded-full ${eventTypeColor[ev.event_type] ?? severityDot[ev.severity] ?? "bg-muted"}`} />
                  <span className="tabular-nums text-muted-foreground w-32 shrink-0">{new Date(ev.created_at).toLocaleString()}</span>
                  <span className="uppercase tracking-wide text-muted-foreground w-44 truncate">{ev.event_type.replace(/_/g, " ")}</span>
                  <span className="flex-1 truncate">{ev.message || ev.title}</span>
                  {ev.repair_attempt && <Badge variant="outline" className="text-[10px]">#{ev.repair_attempt}</Badge>}
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      <section>
        <h3 className="font-semibold mb-1">Validation errors ({errors.length})</h3>
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {errors.map((e: any, i: number) => (
            <div key={i} className="text-xs flex gap-2 items-start">
              <Badge variant="outline" className={e.severity === "error" ? "text-red-300" : "text-amber-300"}>
                {e.code}
              </Badge>
              <span className="text-muted-foreground">{e.field}</span>
              <span>{e.message}</span>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h3 className="font-semibold mb-1">Repair history ({history.length})</h3>
        <div className="space-y-2 max-h-48 overflow-y-auto text-xs">
          {history.map((h: any, i: number) => (
            <div key={i} className="border border-border rounded p-2">
              <div className="text-muted-foreground">Attempt #{h.attempt ?? i + 1} · {h.ts ?? ""}</div>
              {h.notes && <div>Notes: {(h.notes ?? []).join(", ")}</div>}
              {h.handledCodes && <div>Handled: {(h.handledCodes ?? []).join(", ")}</div>}
              {h.patch && (
                <pre className="mt-1 bg-muted/40 p-1 rounded overflow-x-auto">
                  {JSON.stringify(h.patch, null, 2).slice(0, 400)}
                </pre>
              )}
            </div>
          ))}
        </div>
      </section>

      {item.id && (item.publish_status === "NEEDS_REVIEW" || item.status === "NEEDS_REVIEW") && (
        <div className="flex gap-2 pt-2 border-t border-border">
          <Button size="sm" variant="outline" onClick={() => onAction("retry", item.id ?? item.post_id, item.kind)}>
            <RefreshCw className="h-3 w-3" />Retry
          </Button>
          <Button size="sm" onClick={() => onAction("approve", item.id ?? item.post_id, item.kind)}>Approve</Button>
          <Button size="sm" variant="destructive" onClick={() => onAction("reject", item.id ?? item.post_id, item.kind)}>Reject</Button>
        </div>
      )}
    </div>
  );
};

function SlugAnalyticsView() {
  const [loading, setLoading] = useState(true);
  const [malformed, setMalformed] = useState<any[]>([]);
  const [repairs, setRepairs] = useState<any[]>([]);

  const reload = useCallback(async () => {
    setLoading(true);
    const [{ data: posts }, { data: events }] = await Promise.all([
      supabase
        .from("blog_posts")
        .select("id, slug, client_id, slug_aliases, published, created_at")
        .order("created_at", { ascending: false })
        .limit(2000),
      supabase
        .from("brain_events")
        .select("id, created_at, entity_id, client_id, message, metadata, source")
        .eq("event_type", "slug_normalized")
        .order("created_at", { ascending: false })
        .limit(200),
    ]);

    const malformedRe = /^[a-z0-9]+(-[a-z0-9]+)*$/;
    const legacyTail = /(-(en|fr))(-|$)|-[0-9]{6,}(-[0-9]+)?$|-pillar(-[0-9]+)?$|-(en|fr)$/i;
    const bad = (posts ?? []).filter((p: any) =>
      p.slug && (!malformedRe.test(p.slug) || legacyTail.test(p.slug) || p.slug.length > 80)
    );
    setMalformed(bad);
    setRepairs(events ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Top patterns in repaired old slugs
  const topPatterns = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const ev of repairs) {
      const old: string | undefined = ev?.metadata?.old_slugs?.slice?.(-1)?.[0];
      if (!old) continue;
      let pattern = "other";
      if (/-[0-9]{6,}(-[0-9]+)?$/.test(old)) pattern = "timestamp/id tail";
      else if (/-pillar(-[0-9]+)?$/.test(old)) pattern = "-pillar suffix";
      else if (/-(en|fr)$/i.test(old) || /-(en|fr)-/i.test(old)) pattern = "language tail (-en/-fr)";
      else if (old.length > 80) pattern = "over 80 chars";
      counts[pattern] = (counts[pattern] || 0) + 1;
    }
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [repairs]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Malformed slugs (live)</CardTitle></CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold ${malformed.length === 0 ? "text-emerald-400" : "text-red-400"}`}>
              {loading ? "—" : malformed.length}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {malformed.length === 0 ? "All slugs canonical ✓" : "Need repair"}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Repairs logged</CardTitle></CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{loading ? "—" : repairs.length}</div>
            <div className="text-xs text-muted-foreground mt-1">slug_normalized brain events</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Top malformed pattern</CardTitle></CardHeader>
          <CardContent>
            <div className="text-lg font-semibold">{topPatterns[0]?.[0] ?? "—"}</div>
            <div className="text-xs text-muted-foreground mt-1">{topPatterns[0]?.[1] ?? 0} occurrences</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Top malformed patterns</CardTitle>
          <Button size="sm" variant="ghost" onClick={reload}><RefreshCw className="h-3 w-3 mr-1" />Refresh</Button>
        </CardHeader>
        <CardContent>
          {topPatterns.length === 0 ? (
            <div className="text-sm text-muted-foreground">No repaired slugs recorded yet.</div>
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead>Pattern</TableHead><TableHead className="text-right">Count</TableHead></TableRow></TableHeader>
              <TableBody>
                {topPatterns.map(([p, n]) => (
                  <TableRow key={p}><TableCell>{p}</TableCell><TableCell className="text-right font-mono">{n}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {malformed.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base text-red-400">Malformed slugs needing repair</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader><TableRow><TableHead>Slug</TableHead><TableHead>Published</TableHead></TableRow></TableHeader>
              <TableBody>
                {malformed.slice(0, 50).map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-mono text-xs">{p.slug}</TableCell>
                    <TableCell>{p.published ? <Badge className="bg-emerald-500/20 text-emerald-300">live</Badge> : <Badge variant="secondary">no</Badge>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">Recent slug repairs</CardTitle></CardHeader>
        <CardContent>
          {repairs.length === 0 ? (
            <div className="text-sm text-muted-foreground">No repairs logged yet.</div>
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead>When</TableHead><TableHead>New slug</TableHead><TableHead>Old slug</TableHead></TableRow></TableHeader>
              <TableBody>
                {repairs.slice(0, 30).map((ev) => {
                  const aliases: string[] = ev?.metadata?.old_slugs ?? [];
                  return (
                    <TableRow key={ev.id}>
                      <TableCell className="text-xs text-muted-foreground">{new Date(ev.created_at).toLocaleString()}</TableCell>
                      <TableCell className="font-mono text-xs">{ev?.metadata?.new_slug ?? "—"}</TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground truncate max-w-[280px]">{aliases[aliases.length - 1] ?? "—"}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default BrainCenter;
